import React from "react";
import { Phone, Mail, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import api from "@/lib/api";
import { translateFr as tfr } from "@/i18n";

/**
 * Smart Relance:
 * - Priority: player.phone / player.email > club.contact_phone / club.contact_email
 * - Mobile (<768px): tel: if any phone exists, else mailto:
 * - Desktop: if both phone & email available -> dropdown to choose; else available one
 * - If nothing available -> error toast "Aucun contact renseigné"
 *
 * `context` ("card" | "sheet" | "list") disambiguates data-testid when the same
 * player has buttons rendered in multiple places simultaneously.
 */
export function useRelance({ player, club, onLogged }) {
  const phone = (player?.phone || "").trim() || (club?.contact_phone || "").trim();
  const email = (player?.email || "").trim() || (club?.contact_email || "").trim();
  const hasPhone = !!phone;
  const hasEmail = !!email;

  const log = async (type) => {
    try {
      await api.post("/interactions", {
        player_id: player.id,
        type,
        note: `Relance ${type === "call" ? "appel" : "email"}`,
      });
      toast.success(tfr("Relance enregistrée"));
      onLogged?.();
    } catch (error) {
      console.error("[RelanceButton] log interaction failed:", error);
    }
  };

  const callPhone = (e) => {
    e?.stopPropagation?.();
    if (!hasPhone) { toast.error(tfr("Aucun numéro renseigné")); return; }
    window.location.href = `tel:${phone.replace(/\s/g, "")}`;
    log("call");
  };

  const sendEmail = (e) => {
    e?.stopPropagation?.();
    if (!hasEmail) { toast.error(tfr("Aucun email renseigné")); return; }
    const subject = encodeURIComponent(`${tfr("À propos de")} ${player.name}`);
    const body = encodeURIComponent(
      `Bonjour,\n\nJe reviens vers vous au sujet de ${player.name}${player.position ? ` (${player.position})` : ""}.\n\nCordialement.`
    );
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
    log("email");
  };

  return { phone, email, hasPhone, hasEmail, callPhone, sendEmail };
}

export default function RelanceButton({ player, club, size = "icon", className, onLogged, context = "card" }) {
  const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;
  const { hasPhone, hasEmail, callPhone, sendEmail } = useRelance({ player, club, onLogged });
  const tid = `relance-btn-${context}-${player?.id || "x"}`;

  // No contact at all
  if (!hasPhone && !hasEmail) {
    if (size === "full") {
      return (
        <Button
          variant="outline"
          disabled
          className={cn("gap-2 cursor-not-allowed text-muted-foreground", className)}
          data-testid={tid}
          title={tfr("Aucun contact renseigné")}
        >
          <AlertCircle className="h-4 w-4" /> Aucun contact
        </Button>
      );
    }
    return (
      <button
        onClick={(e) => { e.stopPropagation(); toast.error(tfr("Aucun contact renseigné pour ce joueur")); }}
        className={cn(
          "rounded-full p-1.5 text-muted-foreground hover:bg-surface-hover transition-colors opacity-60",
          className
        )}
        data-testid={tid}
        title={tfr("Aucun contact renseigné")}
        aria-label={tfr("Aucun contact")}
      >
        <AlertCircle className="h-4 w-4" />
      </button>
    );
  }

  // Mobile: tel preferred, fallback mailto
  if (isMobile) {
    const action = hasPhone ? callPhone : sendEmail;
    if (size === "full") {
      return (
        <Button
          onClick={action}
          data-testid={tid}
          className={cn("bg-primary text-primary-foreground hover:bg-primary/90 gap-2 active:scale-[0.97] transition-transform duration-150", className)}
        >
          {hasPhone ? <Phone className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
          {tfr("Relance")}
        </Button>
      );
    }
    return (
      <button
        onClick={action}
        data-testid={tid}
        className={cn("rounded-full p-1.5 text-foreground/70 hover:text-foreground hover:bg-secondary transition-colors", className)}
        title={tfr("Relance")}
        aria-label={tfr("Relance")}
      >
        {hasPhone ? <Phone className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
      </button>
    );
  }

  // Desktop: if both available, show dropdown; else single action
  if (hasPhone && hasEmail) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {size === "full" ? (
            <Button
              data-testid={tid}
              className={cn("bg-primary text-primary-foreground hover:bg-primary/90 gap-2 active:scale-[0.97] transition-transform duration-150", className)}
              onClick={(e) => e.stopPropagation()}
            >
              <Mail className="h-4 w-4" /> Relance
            </Button>
          ) : (
            <button
              onClick={(e) => e.stopPropagation()}
              data-testid={tid}
              className={cn("rounded-full p-1.5 text-foreground/70 hover:text-foreground hover:bg-secondary transition-colors", className)}
              title={tfr("Relance")}
              aria-label={tfr("Relance")}
            >
              <Mail className="h-4 w-4" />
            </button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem onClick={sendEmail} data-testid={`relance-email-${context}-${player?.id}`}>
            <Mail className="h-4 w-4 mr-2" /> Envoyer un email
          </DropdownMenuItem>
          <DropdownMenuItem onClick={callPhone} data-testid={`relance-call-${context}-${player?.id}`}>
            <Phone className="h-4 w-4 mr-2" /> Appeler
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Desktop with only one channel
  const single = hasEmail ? sendEmail : callPhone;
  const Icon = hasEmail ? Mail : Phone;
  if (size === "full") {
    return (
      <Button
        onClick={single}
        data-testid={tid}
        className={cn("bg-primary text-primary-foreground hover:bg-primary/90 gap-2 active:scale-[0.97] transition-transform duration-150", className)}
      >
        <Icon className="h-4 w-4" /> Relance
      </Button>
    );
  }
  return (
    <button
      onClick={single}
      data-testid={tid}
      className={cn("rounded-full p-1.5 text-foreground/70 hover:text-foreground hover:bg-secondary transition-colors", className)}
      title={tfr("Relance")}
      aria-label={tfr("Relance")}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}
